import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { formatRupiah } from "@/lib/format";
import { Scissors } from "lucide-react";

export default function InvoicePrint() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [{ data: o }, { data: s }] = await Promise.all([
          api.get(`/orders/${id}`),
          api.get("/settings")
        ]);
        setOrder(o);
        setSettings(s || {});
        setTimeout(() => {
          try {
            window.print();
          } catch (e) {
            console.error(e);
          }
        }, 500);
      } catch (err) {
        setError(true);
      }
    };
    fetchData();
  }, [id]);

  if (error) return <div className="p-8 text-red-500">Gagal memuat invoice.</div>;
  if (!order) return <div className="p-8">Memuat invoice...</div>;

  const totalBeforeDiscount = order.items.reduce((acc, it) => acc + (it.unit_price * it.quantity), 0);
  const sisaTagihan = Math.max(0, order.total - (order.dp_amount || 0));

  return (
    <div className="bg-white text-stone-900 font-sans min-h-screen p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-3">
          {settings?.logoUrl ? (
            <img src={settings.logoUrl} alt="Logo" className="h-16 w-16 object-contain" />
          ) : (
            <div className="h-16 w-16 border border-[#B89968]/40 bg-[#B89968]/10 flex items-center justify-center overflow-hidden">
              <Scissors className="h-8 w-8 text-[#D9C39A]" strokeWidth={1.5} />
            </div>
          )}
          <div>
            <h1 className="text-xl font-bold text-stone-800 tracking-tight">
              {settings?.brandName || "CLF"}
            </h1>
            <p className="text-stone-500 text-sm">{settings?.slogan || "Tailor Order"}</p>
          </div>
        </div>
        <div className="text-right">
          <h2 className="text-5xl font-black text-[#B5692A] tracking-tighter italic">INVOICE</h2>
        </div>
      </div>

      <div className="h-1 w-full bg-[#B5692A] mb-8" />

      {/* Info */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <h3 className="text-xl font-bold text-stone-800">{order.customer?.name}</h3>
          <p className="text-sm text-stone-600 font-medium">{order.customer?.company || ""}</p>
          <div className="text-sm text-stone-500 mt-2 max-w-[250px] whitespace-pre-wrap">
            {order.customer?.address || "-"}
          </div>
          <p className="text-sm text-stone-500 mt-1">{order.customer?.phone || "-"}</p>
        </div>
        <div className="text-right">
          <p className="text-sm font-bold text-stone-500 uppercase tracking-widest mb-1">INVOICE#</p>
          <p className="text-lg text-stone-800">{order.order_number}</p>
        </div>
      </div>

      {/* Table */}
      <table className="w-full mb-8 text-sm">
        <thead>
          <tr className="bg-[#B5692A] text-white">
            <th className="py-3 px-4 text-left font-semibold uppercase">Product</th>
            <th className="py-3 px-4 text-center font-semibold uppercase">Price</th>
            <th className="py-3 px-4 text-center font-semibold uppercase">Qty</th>
            <th className="py-3 px-4 text-right font-semibold uppercase">Total</th>
          </tr>
        </thead>
        <tbody>
          {order.items.map((it, idx) => (
            <tr key={idx} className={idx % 2 === 0 ? "bg-stone-50" : "bg-white"}>
              <td className="py-3 px-4 text-stone-800">
                {it.product_type} {it.product_name ? `- ${it.product_name}` : ""}
              </td>
              <td className="py-3 px-4 text-center text-stone-600">{formatRupiah(it.unit_price)}</td>
              <td className="py-3 px-4 text-center text-stone-600">{it.quantity}</td>
              <td className="py-3 px-4 text-right text-stone-800 font-medium">
                {formatRupiah(it.unit_price * it.quantity)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <div className="flex justify-between items-start mb-12">
        <div className="text-xs text-stone-600 space-y-1">
          <div className="font-bold text-stone-800 mb-2 uppercase">Pembayaran Di Transfer Ke:</div>
          <div className="whitespace-pre-wrap">{settings?.invoiceBank || "BANK : BCA\nACCOUNT : 123456789\nNAMA : CLF Tailor"}</div>
        </div>
        <div className="w-72 space-y-2 text-sm">
          <div className="flex justify-between font-bold text-stone-800">
            <span>SUBTOTAL</span>
            <span>{formatRupiah(totalBeforeDiscount)}</span>
          </div>
          {Number(order.discount) > 0 && (
            <div className="flex justify-between text-stone-600">
              <span>DISKON</span>
              <span>- {formatRupiah(order.discount)}</span>
            </div>
          )}
          <div className="flex justify-between text-stone-600">
            <span>DP</span>
            <span>{formatRupiah(order.dp_amount || 0)}</span>
          </div>
          <div className="flex justify-between font-bold text-stone-900 pt-2 border-t border-stone-200">
            <span>KEKURANGAN</span>
            <span>{formatRupiah(sisaTagihan)}</span>
          </div>
        </div>
      </div>

      {/* Syarat & Ketentuan */}
      <div className="border-t-2 border-stone-100 pt-6">
        <h4 className="font-bold text-stone-800 mb-2 text-sm uppercase">Syarat & Ketentuan</h4>
        <div className="text-xs text-stone-500 whitespace-pre-wrap">
          {settings?.invoiceNotes || `1. Pembayaran sah hanya melalui transfer ke rekening di atas.
2. Pelunasan wajib dilakukan maksimal 1x24 jam sebelum pengiriman.
3. Estimasi pengerjaan sesuai dengan kesepakatan tertulis.
4. Perubahan desain setelah produksi akan dikenakan biaya tambahan.
5. Komplain wajib disertai video unboxing maksimal 2x24 jam setelah diterima.`}
        </div>
      </div>
      
      {/* Contact Footer */}
      <div className="flex justify-between items-start mt-12 pt-6 border-t-2 border-stone-100 text-xs text-stone-600">
        <div>
          <div className="font-bold text-stone-800 mb-1">Phone.</div>
          <div>{settings?.invoicePhone || "-"}</div>
        </div>
        <div>
          <div className="font-bold text-stone-800 mb-1">Email.</div>
          <div>{settings?.invoiceEmail || "-"}</div>
        </div>
        <div className="text-right">
          <div className="font-bold text-stone-800 mb-1">Alamat.</div>
          <div className="max-w-[200px] whitespace-pre-wrap">{settings?.invoiceAddress || "-"}</div>
        </div>
      </div>
    </div>
  );
}
